import { useMemo } from 'react';
import { z } from 'zod';

import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { WHEN_TO_USE_EXPLAINER } from '@/constants/medicalOnDemand';
import { useFeatureFlag, useFeatureFlagValue } from '@/hooks/useFeatureFlag';

/**
 * Every field optional, and validated rather than trusted.
 *
 * The value is hand-authored in Flagsmith, so a typo or a half-finished edit
 * has to degrade rather than throw inside a modal a clinician has just opened.
 * Optional-per-field is what lets someone change one heading without restating
 * the comparison rows and the four examples, which is where a whole-object
 * schema would invite copy-paste mistakes in the parts nobody meant to touch.
 */
const explainerCopySchema = z.object({
  title: z.string().min(1).optional(),
  intro: z.string().min(1).optional(),
  expertColumnHeading: z.string().min(1).optional(),
  teamColumnHeading: z.string().min(1).optional(),
  examplesHeading: z.string().min(1).optional(),
  exampleVerdictLabel: z.string().min(1).optional(),
  expertCtaLabel: z.string().min(1).optional(),
  linkLabel: z.string().min(1).optional(),
  bulletRows: z
    .array(z.object({ expert: z.string().min(1), team: z.string().min(1) }))
    .min(1)
    .optional(),
  examples: z
    .array(z.object({ question: z.string().min(1), destination: z.enum(['expert', 'team']) }))
    .min(1)
    .optional(),
});

export type WhenToUseExplainerCopy = {
  title: string;
  intro?: string;
  expertColumnHeading: string;
  teamColumnHeading: string;
  examplesHeading: string;
  exampleVerdictLabel: string;
  expertCtaLabel: string;
  linkLabel: string;
  bulletRows: { expert: string; team: string }[];
  examples: { question: string; destination: 'expert' | 'team' }[];
};

/**
 * The explainer's copy, with a Flagsmith value layered over the approved
 * in-repo set.
 *
 * Field by field rather than all or nothing. Setting `title` alone changes the
 * title and leaves the rows and examples as written, and a malformed value
 * changes nothing at all. That is the difference from `useFellowSeedQA`, which
 * has no in-repo set to fall back to and so degrades to an empty list.
 *
 * The in-repo copy is Vicki's and is pinned by a test. A flag value bypasses
 * that pin by design, which is the point of the flag and also its risk, so copy
 * set here needs the same approval and should land back in the constants once
 * it settles. See the flag's own comment.
 *
 * `intro` is the one field the caller still composes, because it takes the
 * flag-resolved team name and an override would have to carry that
 * substitution itself. An override here replaces the whole sentence.
 */
/**
 * Whether the marker, the link and the modal render at all.
 *
 * One Flagsmith flag carries both halves of this feature, because they are the
 * same feature: `enabled` says whether it is on, `value` says what it says.
 * Splitting them across two flags would let the copy outlive its gate, and
 * would put "turn this off" and "fix a typo" in different places on the day
 * both are urgent.
 *
 * Off by default, so merging this does not put it in front of anyone. It is
 * the kill switch for the promotional email: turning the flag off retires the
 * marker, the link and the modal together without a deploy.
 */
export function useWhenToUseExplainerEnabled(): boolean {
  return useFeatureFlag(FEATURE_FLAGS.MOD_WHEN_TO_ASK);
}

export function useWhenToUseExplainerCopy(): WhenToUseExplainerCopy {
  const flagValue = useFeatureFlagValue(FEATURE_FLAGS.MOD_WHEN_TO_ASK);

  return useMemo(() => {
    const parsed = explainerCopySchema.safeParse(flagValue);

    const override = parsed.success ? parsed.data : {};

    return {
      title: override.title ?? WHEN_TO_USE_EXPLAINER.title,
      intro: override.intro,
      expertColumnHeading:
        override.expertColumnHeading ?? WHEN_TO_USE_EXPLAINER.expertColumnHeading,
      teamColumnHeading: override.teamColumnHeading ?? WHEN_TO_USE_EXPLAINER.teamColumnHeading,
      examplesHeading: override.examplesHeading ?? WHEN_TO_USE_EXPLAINER.examplesHeading,
      exampleVerdictLabel:
        override.exampleVerdictLabel ?? WHEN_TO_USE_EXPLAINER.exampleVerdictLabel,
      expertCtaLabel: override.expertCtaLabel ?? WHEN_TO_USE_EXPLAINER.expertCtaLabel,
      linkLabel: override.linkLabel ?? WHEN_TO_USE_EXPLAINER.linkLabel,
      bulletRows: override.bulletRows ?? WHEN_TO_USE_EXPLAINER.bulletRows,
      examples: override.examples ?? WHEN_TO_USE_EXPLAINER.examples,
    };
  }, [flagValue]);
}
